// Fill the Blanks: a sentence with a gap and four choices. Grammar by feel, with a one-line
// why after every answer, and a silly twist now and then.

import { h, clear } from '@kit/dom';
import { shuffle } from '@kit/rng';
import type { GameModule, GameContext } from '@shell/types';
import { SENTENCES, sentenceBandFor, fillBlank, type BlankItem } from '@content/words/sentences';

const ITEMS_PER_ROUND = 8;

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  const band = sentenceBandFor(level);
  const items: BlankItem[] = shuffle(ctx.rng, SENTENCES[band]).slice(0, ITEMS_PER_ROUND);
  let idx = 0;
  let attempts = 0, correct = 0;
  let missedThis = false;
  let locked = false;
  const t0 = performance.now();
  let tItem = performance.now();

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Sentence ', h('strong', null, '1'), ` of ${items.length}`), h('span.muted.small', null, 'Pick the word that fits'));
  const text = h('p.prompt', { style: { fontSize: '22px', lineHeight: '1.35', minHeight: '80px' } });
  const choices = h('div.choices');
  const why = h('p.center.muted', { style: { minHeight: '44px', fontWeight: '700' } });
  const silly = h('div.card.center', { style: { display: 'none', borderColor: 'var(--fun)' } });
  host.append(head, text, choices, why, silly);

  function cur(): BlankItem { return items[idx]; }

  function show() {
    (head.querySelector('strong') as HTMLElement).textContent = String(idx + 1);
    text.textContent = cur().text.replace(/___/g, '_____');
    why.textContent = '';
    silly.style.display = 'none';
    missedThis = false; locked = false;
    tItem = performance.now();
    clear(choices);
    const order = shuffle(ctx.rng, [0, 1, 2, 3]);
    for (const ci of order) {
      const label = cur().choices[ci];
      choices.append(h('button.choice', { onclick: () => choose(ci), style: { fontSize: label.length > 12 ? '17px' : '20px', padding: '6px 8px', textTransform: 'none' } }, label));
    }
  }

  function choose(ci: number) {
    if (locked) return;
    const it = cur();
    const ms = performance.now() - tItem;
    const btns = [...choices.querySelectorAll('button')] as HTMLButtonElement[];
    const btn = btns.find(b => b.textContent === it.choices[ci]);
    if (ci === it.answer) {
      locked = true;
      if (!missedThis) { attempts++; correct++; }
      ctx.report({ skill: 'grammar', level, correct: !missedThis, hintUsed: missedThis, ms, problemId: `blank:${band}:${it.text.slice(0, 20)}` });
      ctx.sound('ding');
      btn?.classList.add('good');
      text.textContent = fillBlank(it.text, it.choices[it.answer]);
      why.textContent = it.why;
      // silly twist about one time in four, only on a clean answer
      if (!missedThis && ctx.rng() < 0.25) {
        silly.style.display = '';
        clear(silly);
        silly.append(h('p.small.muted', null, 'Silly mode:'), h('p', { style: { fontWeight: '800' } }, fillBlank(it.text, it.choices[it.silly])));
        setTimeout(next, 2600);
      } else {
        setTimeout(next, 1700);
      }
    } else {
      if (!missedThis) { attempts++; missedThis = true; ctx.report({ skill: 'grammar', level, correct: false, hintUsed: false, ms, problemId: `blank:${band}:${it.text.slice(0, 20)}` }); }
      ctx.sound('buzz');
      if (btn) { btn.classList.add('bad'); btn.disabled = true; }
      why.textContent = `Not "${it.choices[ci]}". Read it out loud and try another.`;
    }
  }

  function next() {
    idx++;
    if (idx >= items.length) {
      if (attempts === correct && attempts >= ITEMS_PER_ROUND && level >= 5) ctx.charm('charm.grammar');
      ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 });
      return;
    }
    level = ctx.currentLevel();
    show();
  }

  show();
  return { destroy() { clear(host); } };
}

const mod: GameModule = {
  id: 'words.fill-blanks',
  title: 'Fill the Blanks',
  blurb: 'Pick the word that makes the sentence work. Sometimes it gets silly.',
  category: 'words',
  skills: ['grammar'],
  levelRange: [1, 10],
  unlock: { stars: 5 },
  mount,
};
export default mod;
